import { exportBackup, type ProgressFn } from "./exportBackup";
import type { ClinicalOSBackupFile } from "./types";
import { stableUuid, isUuid } from "./id";

export interface PiruNativeDose {
  id?: string;
  substance: string;
  amount: number;
  unit: string;
  route: string;
  timestamp: number;
  tags: string[];
  isBackgroundMed: boolean;
  displayNameSnapshot?: string;
  substanceUID?: string;
  isomer?: string;
  releaseForm?: string;
  productName?: string;
  saltForm?: string;
  notes?: string;
  isApproximate?: boolean;
  isUnknownDose?: boolean;
  hadGrapefruit?: boolean;
  meal?: string;
  volumeML?: number;
  abv?: number;
  drinkName?: string;
  combinationName?: string;
  latitude?: number;
  longitude?: number;
  locationName?: string;
}

export interface PiruNativeNote {
  id: string;
  timestamp: number;
  text: string;
  kind: string;
  descriptors: string[];
  mood?: number;
  energy?: number;
  social?: number;
  worked?: number;
  heartRate?: number;
  shulgin?: number;
}

export interface PiruNativeSession {
  id: string;
  startDate: number;
  title?: string;
  note?: string;
  doses: PiruNativeDose[];
  notes: PiruNativeNote[];
}

export interface PiruNativeFile {
  piruExportVersion: 1;
  appVersion: string;
  exportedAt: number;
  sessions: PiruNativeSession[];
  orphanDoses: PiruNativeDose[];
  dailyDoseItems: Record<string, unknown>[];
  substanceColors: Record<string, unknown>[];
  favorites: Record<string, unknown>[];
  customSubstances: Record<string, unknown>[];
  inventory: Record<string, unknown>[];
}

type Row = Record<string, any>;
const rows = (tables: Record<string, Row[]>, table: string): Row[] => tables[table] ?? [];
const text = (value: unknown): string => String(value ?? "").trim();
const normalized = (value: unknown): string => text(value).toLocaleLowerCase("pt-BR").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const finite = (value: unknown): number | null => {
  const n = typeof value === "number" ? value : value == null || value === "" ? NaN : Number(value);
  return Number.isFinite(n) ? n : null;
};
const epoch = (value: unknown, fallback = Date.now()): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value < 1e12 ? value * 1000 : value;
  if (typeof value === "string" && value.trim()) {
    const n = Number(value);
    if (/^\d+$/.test(value.trim()) && Number.isFinite(n)) return n < 1e12 ? n * 1000 : n;
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
};
const dateText = (value: unknown): string | null => {
  const n = epoch(value, NaN);
  return Number.isFinite(n) ? new Date(n).toISOString() : null;
};

export function piruRoute(value: unknown): string {
  const route = normalized(value).replace(/[._-]+/g, " ").trim();
  if (["oral", "po", "via oral", "oral ir", "oral er"].includes(route)) return "oral";
  if (["sublingual", "sublingua"].includes(route)) return "sublingual";
  if (["buccal", "bucal", "pouch", "snus"].includes(route)) return "buccal";
  if (["nasal", "intranasal", "insufflation", "insufflated"].includes(route)) return "insufflation";
  if (["inhaled", "inhalation", "smoked", "vaporized", "vaporizado", "fumado", "inalatorio", "inalada", "inalado"].includes(route)) return "inhalation";
  if (["iv", "i v", "intravenous", "intravenosa", "intravenoso"].includes(route)) return "intravenous";
  if (["im", "i m", "intramuscular"].includes(route)) return "intramuscular";
  if (["sc", "subcutanea", "subcutaneous", "subcutaneo"].includes(route)) return "subcutaneous";
  if (["transdermal", "transdermico", "transdermica", "topical", "topica", "topico"].includes(route)) return "transdermal";
  if (["rectal", "retal", "plugged"].includes(route)) return "rectal";
  return "other";
}

function asPiruDose(row: Row, extraMetadata: Row = {}): PiruNativeDose | null {
  const at = dateText(row.actual_time);
  if (!at) return null;
  const amount = finite(row.dose_amount);
  const substance = text(row.substance_name) || text(row.substances?.name) || "Substância não identificada";
  const metadata = extraMetadata && typeof extraMetadata === "object" ? extraMetadata : {};
  const tags = Array.from(new Set([
    ...(Array.isArray(metadata.tags) ? metadata.tags.map(String) : []),
    row.dose_goal ? "objetivo:" + row.dose_goal : "",
    row.log_type && !["dose", "pontual", "manutencao"].includes(normalized(row.log_type)) ? "registro:" + row.log_type : "",
    row.purity_status ? "pureza:" + row.purity_status : "",
    finite(row.purity_percent) != null ? "pureza_pct:" + finite(row.purity_percent) : "",
  ].filter(Boolean)));
  return {
    ...(isUuid(row.id) ? { id: row.id } : {}),
    substance,
    amount: amount ?? 0,
    unit: text(row.dose_unit) || "unknown",
    route: piruRoute(row.route),
    timestamp: epoch(row.actual_time),
    tags,
    isBackgroundMed: /manutenc|background|daily/.test(normalized(row.log_type)) || /manutenc/.test(normalized(row.dose_goal)),
    displayNameSnapshot: text(row.formulation_name) || text(row.formulation) || undefined,
    productName: text(row.brand_name) || undefined,
    releaseForm: text(metadata.release_form) || undefined,
    saltForm: text(metadata.salt_form) || undefined,
    isomer: text(metadata.isomer) || undefined,
    notes: [
      text(row.patient_notes) || text(row.notes_patient),
      text(row.notes_clinician),
      text(row.perceived_effect_text) ? "Efeito percebido: " + text(row.perceived_effect_text) : "",
      text(row.expected_effect_text) ? "Efeito esperado: " + text(row.expected_effect_text) : "",
      finite(row.benefit_0_100) != null ? "Benefício: " + finite(row.benefit_0_100) + "/100" : "",
      finite(row.adverse_0_100) != null ? "Adversos: " + finite(row.adverse_0_100) + "/100" : "",
      finite(row.caffeine_near_dose_mg) != null ? "Cafeína próxima: " + finite(row.caffeine_near_dose_mg) + " mg" : "",
      finite(row.sleep_hours_prior) != null ? "Sono pré-dose: " + finite(row.sleep_hours_prior) + " h" : "",
      finite(row.sleep_deprivation_at_dose_0_10) != null ? "Privação de sono: " + finite(row.sleep_deprivation_at_dose_0_10) + "/10" : "",
      text(metadata.source_app) && text(metadata.source_app) !== "piru" ? "Origem: " + text(metadata.source_app) : "",
    ].filter(Boolean).join("\n") || undefined,
    isApproximate: metadata.is_approximate === true,
    isUnknownDose: amount == null || metadata.is_unknown_dose === true,
    hadGrapefruit: metadata.had_grapefruit === true,
    meal: ["empty", "light", "full"].includes(normalized(metadata.meal ?? row.food_context)) ? normalized(metadata.meal ?? row.food_context) : undefined,
    volumeML: finite(row.beverage_consumed_ml) ?? undefined,
    abv: finite(row.beverage_abv_percent) ?? undefined,
    drinkName: text(row.beverage_name) || undefined,
    combinationName: text(metadata.combination_name) || undefined,
    locationName: text(metadata.location_name) || undefined,
    latitude: finite(metadata.latitude) ?? undefined,
    longitude: finite(metadata.longitude) ?? undefined,
    substanceUID: text(metadata.substance_uid) || undefined,
  };
}

function noteFromEvent(event: Row): PiruNativeNote | null {
  const timestamp = epoch(event.event_at, NaN);
  if (!Number.isFinite(timestamp)) return null;
  const metadata = event.metadata && typeof event.metadata === "object" ? event.metadata : {};
  const title = text(event.title);
  const description = text(event.description);
  const body = [title, description].filter(Boolean).filter((v, i, a) => i === 0 || v !== title).join("\n");
  const numeric = (value: unknown) => finite(value) ?? undefined;
  return {
    id: isUuid(event.id) ? event.id : stableUuid("clinicalos:piru-note", String(event.id ?? "") + "|" + timestamp + "|" + body),
    timestamp,
    text: body || text(metadata.text) || "Evento ClinicalOS · " + (text(event.event_type) || "observação"),
    kind: /experien|subjective|report/.test(normalized(event.event_type)) ? "experience" : "note",
    descriptors: Array.isArray(metadata.descriptors) ? metadata.descriptors.map(String) : [],
    mood: numeric(metadata.mood_0_10 ?? metadata.mood),
    energy: numeric(metadata.energy_0_10 ?? metadata.energy),
    social: numeric(metadata.social),
    worked: numeric(metadata.worked),
    heartRate: numeric(metadata.heart_rate ?? metadata.heartRate),
    shulgin: numeric(metadata.shulgin),
  };
}

function dateKey(timestamp: number): string {
  return new Date(timestamp).toISOString().slice(0, 10);
}

function safeCategory(value: unknown): string {
  const source = normalized(value);
  if (/stimulant|estimulante/.test(source)) return "Stimulant";
  if (/psychedelic|psicodel/.test(source)) return "Psychedelic";
  if (/dissoci/.test(source)) return "Dissociative";
  if (/deliri/.test(source)) return "Deliriant";
  if (/opioid|opioide/.test(source)) return "Opioid";
  if (/benzodiazep/.test(source)) return "Benzodiazepine";
  if (/gaba|gabapentin|pregabalin/.test(source)) return "GABAergic";
  if (/empathogen|entactogen/.test(source)) return "Empathogen";
  if (/cannabinoid|canabinoid/.test(source)) return "Cannabinoid";
  if (/nootropic|nootrop/.test(source)) return "Nootropic";
  if (/eugeroic|wake/.test(source)) return "Eugeroic";
  if (/antidepress|isrs|snri|ssri/.test(source)) return "Antidepressant";
  if (/antipsychotic|antipsic/.test(source)) return "Antipsychotic";
  if (/anticonvuls|antiepil/.test(source)) return "Anticonvulsant";
  if (/analgesic|analges/.test(source)) return "Analgesic";
  if (/antihistamin/.test(source)) return "Antihistamine";
  if (/cardiovascular/.test(source)) return "Cardiovascular";
  if (/antimicrobial|antibiotic|antifung|antiviral/.test(source)) return "Antimicrobial";
  if (/gastrointestinal|gastro/.test(source)) return "Gastrointestinal";
  if (/respiratory|respirat/.test(source)) return "Respiratory";
  if (/endocrine|endocrin/.test(source)) return "Endocrine";
  if (/immunologic/.test(source)) return "Immunological";
  if (/peptide/.test(source)) return "Peptide";
  if (/supplement|suplement/.test(source)) return "Supplement";
  if (/depressant|sedativ|hipnot/.test(source)) return "Depressant";
  return "Other";
}

function timeToMinutes(value: unknown): number | null {
  const match = text(value).match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  return hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59 ? hour * 60 + minute : null;
}

/** Convert an already-exported ClinicalOS patient backup to Piru v1 wire format. */
export function clinicalOSBackupToPiru(backup: ClinicalOSBackupFile, options?: { appVersion?: string; now?: number }): PiruNativeFile {
  const now = options?.now ?? Date.now();
  const tables = backup.data.tables;
  const allSessions = rows(tables, "clinical_sessions");
  const dosesRows = rows(tables, "medication_dose_logs");
  const timelineEvents = rows(tables, "patient_psylog_events").filter((event) => !event.deleted_at);
  const vitals = [
    ...rows(tables, "vital_signs").filter((v) => !v.deleted_at),
    ...rows(tables, "patient_vital_signs").filter((v) => !v.deleted_at),
  ];
  const checkins = rows(tables, "session_checkins").filter((item) => !item.deleted_at);
  const meals = rows(tables, "meal_events").filter((item) => !item.deleted_at);
  const sleep = rows(tables, "sleep_periods").filter((item) => !item.deleted_at);
  const regimenEvents = rows(tables, "patient_medication_regimen_events").filter((item) => !item.deleted_at);
  const inventoryRows = rows(tables, "medication_inventory").filter((item) => !item.deleted_at);
  const inventoryMoves = rows(tables, "medication_inventory_movements");
  const privateSubstances = rows(tables, "substances").filter((s) => s.is_global === false && !s.deleted_at);
  const substanceNames = new Map<string, string>();
  for (const ref of backup.data.substanceRefs ?? []) substanceNames.set(String(ref.id), String(ref.name));
  for (const sub of privateSubstances) if (sub.id) substanceNames.set(String(sub.id), text(sub.name) || text(sub.generic_name));

  const doseMetadata = new Map<string, Row>();
  for (const event of timelineEvents) {
    if (!event.linked_dose_id) continue;
    doseMetadata.set(String(event.linked_dose_id), event.metadata && typeof event.metadata === "object" ? event.metadata : {});
  }
  const sessionById = new Map<string, PiruNativeSession>();
  const sessions: PiruNativeSession[] = [];
  const orphanDoses: PiruNativeDose[] = [];
  const notesBySyntheticDay = new Map<string, PiruNativeSession>();

  for (const session of allSessions) {
    const startDate = epoch(session.session_at ?? session.scheduled_start_at ?? session.created_at, now);
    const id = isUuid(session.id) ? session.id : stableUuid("clinicalos:piru-session", String(session.id ?? "") + "|" + startDate);
    const wire: PiruNativeSession = {
      id,
      startDate,
      title: text(session.name) || "Consulta / registro clínico",
      note: [text(session.notes), text(session.patient_narrative), text(session.clinical_summary), text(session.physician_observation)].filter(Boolean).join("\n") || undefined,
      doses: [],
      notes: [],
    };
    sessions.push(wire);
    sessionById.set(String(session.id), wire);
    sessionById.set(id, wire);
  }

  const sessionForTime = (timestamp: number, title = "ClinicalOS · observações") => {
    const key = dateKey(timestamp);
    let target = notesBySyntheticDay.get(key);
    if (!target) {
      target = {
        id: stableUuid("clinicalos:piru-day", key),
        startDate: new Date(key + "T12:00:00.000Z").getTime(),
        title: title + " · " + key,
        doses: [],
        notes: [],
      };
      notesBySyntheticDay.set(key, target);
      sessions.push(target);
      sessionById.set(target.id, target);
    }
    return target;
  };

  const addNote = (note: PiruNativeNote | null, sessionId?: string | null) => {
    if (!note) return;
    const target = sessionId ? sessionById.get(String(sessionId)) : null;
    (target ?? sessionForTime(note.timestamp)).notes.push(note);
  };

  for (const doseRow of dosesRows) {
    const dose = asPiruDose(doseRow, doseMetadata.get(String(doseRow.id)));
    if (!dose) continue;
    const sourceSessionId = text(doseRow.session_id);
    const targetSession = sourceSessionId ? sessionById.get(sourceSessionId) : null;
    if (targetSession) targetSession.doses.push(dose);
    else orphanDoses.push(dose);
  }

  for (const event of timelineEvents) {
    if (/^dose$/.test(normalized(event.event_type)) || event.linked_dose_id) continue;
    addNote(noteFromEvent(event), event.session_id ?? event.linked_session_id);
  }

  for (const vital of vitals) {
    const timestamp = epoch(vital.measured_at, NaN);
    if (!Number.isFinite(timestamp)) continue;
    const systolic = finite(vital.systolic_mm_hg ?? vital.systolic_bp);
    const diastolic = finite(vital.diastolic_mm_hg ?? vital.diastolic_bp);
    const heartRate = finite(vital.heart_rate_bpm ?? vital.heart_rate);
    const oxygen = finite(vital.oxygen_saturation ?? vital.spo2_percent);
    addNote({
      id: isUuid(vital.id) ? vital.id : stableUuid("clinicalos:piru-vital", String(timestamp) + "|" + systolic + "|" + diastolic + "|" + heartRate),
      timestamp,
      text: [
        "Sinais vitais importados de ClinicalOS",
        systolic != null && diastolic != null ? "PA " + systolic + "/" + diastolic + " mmHg" : "",
        heartRate != null ? "FC " + heartRate + " bpm" : "",
        oxygen != null ? "SpO₂ " + oxygen + "%" : "",
        text(vital.context),
        text(vital.notes),
      ].filter(Boolean).join(" · "),
      kind: "note",
      descriptors: [],
      heartRate: heartRate ?? undefined,
    });
  }

  for (const checkin of checkins) {
    const timestamp = epoch(checkin.checkin_at, NaN);
    if (!Number.isFinite(timestamp)) continue;
    const axisKeys = ["mood_0_10","energy_0_10","focus_0_10","anxiety_0_10","craving_0_10","sedation_0_10","insomnia_0_10","irritability_0_10","impulsivity_0_10"];
    addNote({
      id: isUuid(checkin.id) ? checkin.id : stableUuid("clinicalos:piru-checkin", String(timestamp)),
      timestamp,
      text: [
        "Check-in clínico",
        ...axisKeys.filter((key) => finite(checkin[key]) != null).map((key) => key.replace(/_0_10$/, "") + ": " + finite(checkin[key]) + "/10"),
        text(checkin.patient_report),
        text(checkin.physician_observation),
        text(checkin.context_text),
      ].filter(Boolean).join(" · "),
      kind: "experience",
      descriptors: [],
      mood: finite(checkin.mood_0_10) ?? undefined,
      energy: finite(checkin.energy_0_10) ?? undefined,
    }, checkin.session_id);
  }

  for (const meal of meals) {
    const timestamp = epoch(meal.occurred_at, NaN);
    if (!Number.isFinite(timestamp)) continue;
    addNote({
      id: isUuid(meal.id) ? meal.id : stableUuid("clinicalos:piru-meal", String(timestamp)),
      timestamp,
      text: ["Refeição", text(meal.meal_type), text(meal.description), text(meal.notes), finite(meal.caffeine_mg) != null ? "Cafeína " + finite(meal.caffeine_mg) + " mg" : "", finite(meal.alcohol_units) != null ? "Unidades de álcool " + finite(meal.alcohol_units) : ""].filter(Boolean).join(" · "),
      kind: "note",
      descriptors: [],
    });
  }

  for (const item of sleep) {
    const timestamp = epoch(item.sleep_start_at, NaN);
    if (!Number.isFinite(timestamp)) continue;
    addNote({
      id: isUuid(item.id) ? item.id : stableUuid("clinicalos:piru-sleep", String(timestamp)),
      timestamp,
      text: ["Sono", finite(item.duration_min) != null ? (Number(item.duration_min) / 60).toFixed(1) + " h" : "", finite(item.quality_0_10) != null ? "Qualidade " + finite(item.quality_0_10) + "/10" : "", finite(item.awakenings) != null ? "Despertares " + finite(item.awakenings) : "", text(item.notes)].filter(Boolean).join(" · "),
      kind: "note",
      descriptors: [],
    });
  }

  for (const event of regimenEvents) {
    const timestamp = epoch(event.event_at, NaN);
    if (!Number.isFinite(timestamp)) continue;
    addNote({
      id: isUuid(event.id) ? event.id : stableUuid("clinicalos:piru-regimen-event", String(timestamp)),
      timestamp,
      text: [
        "Tratamento · " + text(event.event_type),
        text(event.metadata?.medication_name),
        event.previous_dose != null || event.new_dose != null ? "Dose " + (event.previous_dose ?? "—") + " → " + (event.new_dose ?? "—") + " " + text(event.dose_unit) : "",
        text(event.reason),
        text(event.clinician_assessment),
        text(event.patient_report),
        text(event.next_step),
      ].filter(Boolean).join(" · "),
      kind: "note",
      descriptors: [],
    }, event.session_id);
  }

  for (const session of sessions) {
    session.doses.sort((a, b) => a.timestamp - b.timestamp);
    session.notes.sort((a, b) => a.timestamp - b.timestamp);
  }
  sessions.sort((a, b) => a.startDate - b.startDate);
  orphanDoses.sort((a, b) => a.timestamp - b.timestamp);

  const dailyDoseItems = rows(tables, "patient_medications")
    .filter((med) => !med.deleted_at && finite(med.current_dose) != null && !/suspens|descontinu|encerr|inactive|paus/.test(normalized(med.status)) && (!med.end_date || epoch(med.end_date, now) >= now))
    .map((med, index) => {
      const substance = substanceNames.get(String(med.substance_id ?? "")) || text(med.free_text_name) || "Medicação";
      const frequency = text(med.frequency) || "daily";
      const asNeeded = /prn|sos|se necessário|se necessario|as needed|eventual/.test(normalized(frequency));
      const startDate = epoch(med.start_date, now);
      const slot = timeToMinutes(med.usual_time);
      const formulation = (backup.data.formulationRefs ?? []).find((ref) => String(ref.id) === String(med.formulation_id));
      return {
        substance,
        amount: finite(med.current_dose) ?? 0,
        unit: text(med.dose_unit) || "mg",
        route: piruRoute(formulation?.route ?? "oral"),
        sortOrder: index,
        category: "Medication",
        isBackgroundMed: true,
        frequencyRaw: frequency,
        frequencyDays: [],
        startDate,
        releaseForm: text(formulation?.formulation_name) || undefined,
        productName: text(med.brand_name) || undefined,
        reminderTimesMinutes: slot != null ? [slot] : [],
        remind: false,
        isAsNeeded: asNeeded,
      };
    });

  // Exporta o estoque como snapshot no instante da exportação. Reexecutar toda a
  // contabilidade histórica dentro do Piru junto das doses importadas pode descontar
  // consumo duas vezes; o backup ClinicalOS segue sendo a fonte do livro-razão completo.
  const inventory = inventoryRows.map((item, index) => {
    const name = text(item.medication_name) || substanceNames.get(String(item.substance_id ?? "")) || "Estoque";
    const notes = text(item.notes);
    const saltMatch = notes.match(/saltForm=([^;]+)/);
    const strengthMatch = notes.match(/unitStrengthMG=([\d.]+)/);
    const doseSizeMatch = text(item.package_description).match(/Tamanho de dose Piru:\s*([\d.]+)/);
    return {
      id: isUuid(item.id) ? item.id : stableUuid("clinicalos:piru-inventory", String(item.id ?? index)),
      sortOrder: index,
      substance: name,
      saltForm: saltMatch?.[1] || undefined,
      unit: text(item.unit) || "un",
      trackingStart: now,
      lowStockThreshold: finite(item.low_stock_threshold) ?? undefined,
      baselineQuantity: finite(item.current_quantity) ?? 0,
      doseSize: finite(doseSizeMatch?.[1]) ?? undefined,
      unitStrengthMG: finite(strengthMatch?.[1]) ?? undefined,
      createdAt: epoch(item.created_at, now),
      manualEvents: [],
    };
  });

  const customSubstances = privateSubstances
    .filter((sub) => isUuid(sub.id) && text(sub.name))
    .map((sub) => {
      // Piru custom substances can carry a dose ladder/duration object that has no
      // direct clinical-equivalent columns. If it was imported from Piru, restore
      // the original wire data instead of silently rewriting harm-reduction tiers
      // as therapeutic dose recommendations.
      const sourceNotes = text(sub.source_notes);
      const sourceMarker = "Conteúdo original do item customizado armazenado para rastreabilidade: ";
      const sourceIndex = sourceNotes.lastIndexOf(sourceMarker);
      let original: Row = {};
      if (sourceIndex >= 0) {
        try { original = JSON.parse(sourceNotes.slice(sourceIndex + sourceMarker.length)); } catch { original = {}; }
      }
      const hasDuration = ["onset", "comeup", "peak", "offset", "afterglow", "total"]
        .some((key) => finite(sub[key + "_min_value"]) != null || finite(sub[key + "_max_value"]) != null);
      const durationRange = (key: string) => {
        const min = finite(sub[key + "_min_value"]);
        const max = finite(sub[key + "_max_value"]);
        return min != null || max != null ? { min: min ?? max ?? 0, max: max ?? min ?? 0 } : undefined;
      };
      const duration = original.duration ?? (hasDuration ? {
        onset: durationRange("onset"),
        comeup: durationRange("comeup"),
        peak: durationRange("peak"),
        offset: durationRange("offset"),
        afterglow: durationRange("residual"),
        total: durationRange("total_duration"),
      } : undefined);
      const halfLifeValue = finite(sub.half_life_max_value) ?? finite(sub.half_life_min_value);
      const halfLifeUnit = normalized(sub.half_life_unit);
      const halfLifeFactor = /min/.test(halfLifeUnit) ? 1 : /day|dia/.test(halfLifeUnit) ? 1440 : /week|semana/.test(halfLifeUnit) ? 10080 : /hour|hora|h\b/.test(halfLifeUnit) ? 60 : 1;
      return {
        id: sub.id,
        name: text(sub.name) || text(sub.generic_name),
        displayName: original.displayName ?? (text(sub.generic_name) || undefined),
        category: original.category ?? safeCategory(sub.pharmacological_class || sub.clinical_category),
        defaultRoute: original.defaultRoute ?? piruRoute(sub.default_route),
        unit: original.unit ?? (text(sub.default_dose_unit) || text(sub.dose_unit_default) || "mg"),
        notes: original.notes ?? text(sub.short_description) ?? "",
        createdAt: epoch(sub.created_at, now),
        halfLifeMinutes: finite(original.halfLifeMinutes) ?? (halfLifeValue != null ? halfLifeValue * halfLifeFactor : undefined),
        doses: original.doses,
        duration,
        components: original.components,
      };
    });

  return {
    piruExportVersion: 1,
    appVersion: "PsicoNorte Clinical / ClinicalOS",
    exportedAt: now,
    sessions,
    orphanDoses,
    dailyDoseItems,
    substanceColors: [],
    favorites: [],
    customSubstances,
    inventory,
  };
}

export function downloadPiruFile(file: PiruNativeFile): { size: number; name: string } {
  const textValue = JSON.stringify(file, null, 2);
  const blob = new Blob([textValue], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const date = new Date(file.exportedAt).toISOString().replace(/[:.]/g, "-");
  const name = "piru-export-" + date + ".json";
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
  return { size: blob.size, name };
}

export async function exportPiruBackup(userId: string, patientId: string, onProgress?: ProgressFn): Promise<{ file: PiruNativeFile; backupBytes: number }> {
  const backup = await exportBackup(userId, {
    kind: "patient",
    patientId,
    includePharmacology: true,
    includeDerived: false,
    includeAuditHistory: false,
  }, onProgress);
  const file = clinicalOSBackupToPiru(backup);
  return { file, backupBytes: new Blob([JSON.stringify(file)]).size };
}
